import { FC, ReactNode, useContext, useEffect } from 'react'
import { Plan, PlanPurchaseExperienceMode } from '@dtx-company/true-common/src/types/Plan'
import { PlanPurchaseExperienceContext } from '@app/common/src/components/PlanPurchaseExperience/PlanPurchaseExperienceContext'
import { PlanType } from '@dtx-company/true-common/src/types/planType'
import { PurchaseExperiencePartnerLogoList } from '@app/common/src/components/PurchaseExperiencePartnerLogoList'
import { Text } from '@dtx-company/shared-components/src/components/atoms/Text/index'
import { UseCouponCodeReturn } from '@app/common/src/hooks/usePromoCodeCoupon'
import {
  _PromoBox,
  _PromoContentBox,
  _PromoCouponBox,
  _PromoFooterBox,
  _PromoHeaderBox,
  _PromoHeaderBoxSmall,
  _PromoHeaderText,
  _PromoImageBox,
  _PromoImageBoxSmall
} from '@app/common/src/components/PlanPurchaseExperience/components/PlanPurchaseExperience.styles'
import Image from 'next/image'

export interface PromoCodeSectionProps {
  promoBackgroundImageUrl?: string
  promoHeading?: ReactNode
  selectedPlan: Plan | undefined
  promoCouponProps?: UseCouponCodeReturn
  mode?: PlanPurchaseExperienceMode
  promoAsset?: ReactNode
  promoHideCompanies?: boolean
}

export const PromoCodeSection: FC<PromoCodeSectionProps> = ({
  promoBackgroundImageUrl,
  promoHeading,
  selectedPlan,
  promoCouponProps,
  mode,
  promoAsset,
  promoHideCompanies
}) => {
  // This is a bit hacky for now to get this working for p1 pro conversion issue
  // to improve AIDA
  // Slightly better would be to base it on the asset image url but since that's
  // a react node now I don't have access to it - these ones all have a specific
  // bg color so using that instead
  const promoImageSmallUrl = (() => {
    if (promoBackgroundImageUrl === '#242424') {
      return '/graphics/lockModal/fc-pro-code-limit3-small-2x.png'
    }
    return '/images/pages/upgrade/pearl-promo-small-2x.png'
  })()

  const context = useContext(PlanPurchaseExperienceContext)

  return (
    <_PromoContentBox component="aside">
      <_PromoBox
        backgroundImageUrl={promoBackgroundImageUrl}
        isTextVersion={promoHeading ? true : false}
      >
        <_PromoHeaderBox component="header">
          <_PromoHeaderText component="p" variant="header/subheader">
            {promoHeading ??
              getDefaultPromoHeading({
                selectedPlanType: selectedPlan?.type,
                isSwitchingBillingCycle: context.isSwitchingBillingCycle,
                isChangePaymentMode: mode === PlanPurchaseExperienceMode.CHANGE_PAYMENT
              })}
          </_PromoHeaderText>
        </_PromoHeaderBox>
        {promoHeading && (
          <_PromoHeaderBoxSmall component="header">
            <_PromoHeaderText component="p" variant="header/subheader">
              {promoHeading}
            </_PromoHeaderText>
          </_PromoHeaderBoxSmall>
        )}
        {!promoHeading && (
          <_PromoImageBoxSmall>
            <Image
              src={promoImageSmallUrl}
              layout="fill"
              alt="Example of a pretend company called Pearl, showing a custom flowcode in dark blue with a double-lined border and a custom flowpage with their logo, ways to contact them, google reviews, and a video."
            />
          </_PromoImageBoxSmall>
        )}
        {promoCouponProps && (
          <_PromoCouponBox>
            <PromoCoupon {...promoCouponProps} />
          </_PromoCouponBox>
        )}
        <_PromoImageBox>
          {promoAsset ?? (
            <Image
              src="/images/pages/upgrade/pearl-promo-2x.png"
              layout="intrinsic"
              width="321"
              height="334"
              alt="Example of a pretend company called Pearl, showing a custom flowcode in dark blue with a double-lined border and a custom flowpage with their logo, ways to contact them, google reviews, a video, and one of their products."
            />
          )}
        </_PromoImageBox>
        {!promoHideCompanies && (
          <_PromoFooterBox component="footer">
            <PurchaseExperiencePartnerLogoList planType={selectedPlan?.type || PlanType.PRO} />
          </_PromoFooterBox>
        )}
      </_PromoBox>
    </_PromoContentBox>
  )
}

export function PromoCoupon(props: UseCouponCodeReturn): JSX.Element {
  const { subtext, setPromoCode, code } = props

  useEffect(() => {
    setPromoCode()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  return (
    <p>
      <Text component="span" variant={'body/small'}>
        {subtext}
      </Text>
      <button
        style={{ background: 'unset', border: 'none', cursor: 'pointer' }}
        onClick={setPromoCode}
      >
        <Text component={'strong'} variant={'header/subheader'}>
          {code}
        </Text>
      </button>
    </p>
  )
}

export function getDefaultPromoHeading({
  selectedPlanType,
  isSwitchingBillingCycle,
  isChangePaymentMode
}: {
  selectedPlanType: PlanType | undefined
  isSwitchingBillingCycle: boolean
  isChangePaymentMode: boolean
}): string {
  // Don't display a heading in the promo area if user is changing billing cycle or payment method
  if (isSwitchingBillingCycle || isChangePaymentMode) return ''
  switch (selectedPlanType) {
    case PlanType.PRO_PLUS_ORG:
      return 'Team collaboration, more data insights, more codes and Flowcode tools to level up your connections.'
    case PlanType.PRO_FLEX:
      return 'The only Flowcode plan you need for scaling real QR growth.'
  }
  return 'Elevate your QR experience with more Flowcodes, Flowpages, advanced customization, and analytics'
}
